import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DollarSign, Save, Plus, Trash2 } from 'lucide-react';
import { useAdmin } from '@/hooks/useAdmin';
import { useAccount, useNetwork } from 'wagmi';
import { toast } from 'sonner';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFirebaseFees } from '@/providers/FirebaseFeeProvider';

interface Collector {
  address: string;
  share: number;
}

interface NetworkFees {
  burnFee: string;
  lockFee: string;
  toolsFee: string;
  scanFee: string;
  masterLockFee: string;
  ttTaxDistribution: string;
  addLiquidityFee: string;
  removeLiquidityFee: string;
  logoFee: string;
  multiSendFee: string;
}

interface FeeData {
  networkFees: {
    [chainId: number]: NetworkFees;
  };
  collectors: Collector[];
}

const SUPPORTED_NETWORKS = [
  { id: 56, name: "BNB Smart Chain", symbol: "BNB" },
  { id: 204, name: "opBNB", symbol: "BNB" },
  { id: 5611, name: "opBNB Testnet", symbol: "BNB" },
  { id: 1, name: "Ethereum", symbol: "ETH" },
  { id: 43114, name: "Avalanche", symbol: "AVAX" },
  { id: 137, name: "Polygon", symbol: "MATIC" },
  { id: 80002, name: "Polygon Amoy", symbol: "MATIC" },
  { id: 1328, name: "Sei Atlantic-2 Testnet", symbol: "SEI" },
  { id: 1329, name: "Sei Pacific-1 Mainnet", symbol: "SEI" }
];

const DEFAULT_NETWORK_FEES: NetworkFees = {
  burnFee: "0.125",
  lockFee: "0.125",
  toolsFee: "0.125",
  scanFee: "0.05",
  masterLockFee: "0.25",
  ttTaxDistribution: "0.25",
  addLiquidityFee: "5",
  removeLiquidityFee: "0",
  logoFee: "0.125",
  multiSendFee: "0.0025"
};

export function FeeSetter() {
  const { isAdmin } = useAdmin();
  const { address } = useAccount();
  const { chain } = useNetwork();
  const { settings: firebaseFees, updateSettings, loading, error } = useFirebaseFees();
  const [selectedNetwork, setSelectedNetwork] = useState<number>(chain?.id || 56);
  const [isLoading, setIsLoading] = useState(false);
  const [localFees, setLocalFees] = useState<FeeData | null>(null);

  useEffect(() => {
    if (firebaseFees) {
      // Ensure all supported networks have fee data
      const updatedFees = { ...firebaseFees };
      SUPPORTED_NETWORKS.forEach(network => {
        if (!updatedFees.networkFees[network.id]) {
          updatedFees.networkFees[network.id] = DEFAULT_NETWORK_FEES;
        }
      });
      setLocalFees(updatedFees);
    }
  }, [firebaseFees]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!localFees) {
    return <div>No fee data available</div>;
  }

  const handleFeeChange = (field: keyof NetworkFees, value: string) => {
    console.log('Handling fee change:', { field, value });
    
    setLocalFees(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        networkFees: {
          ...prev.networkFees,
          [selectedNetwork]: {
            ...prev.networkFees[selectedNetwork],
            [field]: value
          }
        }
      };
    });
  };

  const handleCollectorChange = (index: number, field: keyof Collector, value: string) => {
    setLocalFees(prev => {
      if (!prev) return prev;
      const newCollectors = [...prev.collectors];
      newCollectors[index] = {
        ...newCollectors[index],
        [field]: field === 'share' ? Number(value) : value
      };
      
      return {
        ...prev,
        collectors: newCollectors
      };
    });
  };

  const addCollector = () => {
    setLocalFees(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        collectors: [...prev.collectors, { address: '', share: 0 }]
      };
    });
  };

  const removeCollector = (index: number) => {
    setLocalFees(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        collectors: prev.collectors.filter((_, i) => i !== index)
      };
    });
  };

  const saveFees = async () => {
    if (!address) {
      toast.error('Please connect your wallet');
      return;
    }

    if (!isAdmin) {
      toast.error('Only admin wallets can modify fee settings');
      return;
    }

    if (!localFees) {
      toast.error('No fee data to save');
      return;
    }

    if (!updateSettings) {
      console.error('Update settings function not available');
      toast.error('Unable to save settings - please try logging in again');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Attempting to save fee data:', { localFees, address });
      
      // Create a clean copy of the fees without any undefined values
      const cleanNetworkFees = Object.fromEntries(
        Object.entries(localFees.networkFees).map(([chainId, fees]) => [
          chainId,
          {
            burnFee: String(fees.burnFee || '0'),
            lockFee: String(fees.lockFee || '0'),
            toolsFee: String(fees.toolsFee || '0'),
            scanFee: String(fees.scanFee || '0'),
            masterLockFee: String(fees.masterLockFee || '0'),
            ttTaxDistribution: String(fees.ttTaxDistribution || '0'),
            addLiquidityFee: String(fees.addLiquidityFee || '0'),
            removeLiquidityFee: String(fees.removeLiquidityFee || '0'),
            logoFee: String(fees.logoFee || '0'),
            multiSendFee: String(fees.multiSendFee || '0.0025'),
          },
        ])
      );

      const sanitizedFeeData = {
        networkFees: cleanNetworkFees,
        collectors: localFees.collectors.map(collector => ({
          address: collector.address || '',
          share: Number(collector.share || 0)
        })),
        lastUpdated: new Date().toISOString(),
        updatedBy: address.toLowerCase()
      };

      console.log('Sanitized fee data:', sanitizedFeeData);
      await updateSettings(sanitizedFeeData);
      toast.success('Fee settings saved successfully');
    } catch (error) {
      console.error('Detailed error saving fees:', error);
      if (error instanceof Error) {
        if (error.message === 'Unauthorized') {
          toast.error('You are not authorized to modify fee settings. Please make sure you are using an admin wallet.');
        } else {
          toast.error(`Failed to save fee settings: ${error.message}`);
        }
      } else {
        toast.error('Failed to save fee settings');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentNetworkSymbol = () => {
    return SUPPORTED_NETWORKS.find(n => n.id === selectedNetwork)?.symbol || "BNB";
  };

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="w-6 h-6" />
            Fee Settings
          </h2>
          <Select
            value={selectedNetwork.toString()}
            onValueChange={(value) => setSelectedNetwork(Number(value))}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select Network" />
            </SelectTrigger>
            <SelectContent>
              {SUPPORTED_NETWORKS.map((network) => (
                <SelectItem key={network.id} value={network.id.toString()}>
                  {network.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Fixed {getCurrentNetworkSymbol()} Fees</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground">Burn Fee ({getCurrentNetworkSymbol()})</label>
              <Input
                type="number"
                step="0.001"
                value={localFees.networkFees[selectedNetwork]?.burnFee || ''}
                onChange={(e) => handleFeeChange('burnFee', e.target.value)}
                className="mt-1 bg-background text-foreground"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Tools Fee ({getCurrentNetworkSymbol()})</label>
              <Input
                type="number"
                step="0.001"
                value={localFees.networkFees[selectedNetwork]?.toolsFee || ''}
                onChange={(e) => handleFeeChange('toolsFee', e.target.value)}
                className="mt-1 bg-background text-foreground"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Lock Fee ({getCurrentNetworkSymbol()})</label>
              <Input
                type="number"
                step="0.001"
                value={localFees.networkFees[selectedNetwork]?.lockFee || ''}
                onChange={(e) => handleFeeChange('lockFee', e.target.value)}
                className="mt-1 bg-background text-foreground"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Scan Fee ({getCurrentNetworkSymbol()})</label>
              <Input
                type="number"
                step="0.001"
                value={localFees.networkFees[selectedNetwork]?.scanFee || ''}
                onChange={(e) => handleFeeChange('scanFee', e.target.value)}
                className="mt-1 bg-background text-foreground"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Master Lock Fee ({getCurrentNetworkSymbol()})</label>
              <Input
                type="number"
                step="0.001"
                value={localFees.networkFees[selectedNetwork]?.masterLockFee || ''}
                onChange={(e) => handleFeeChange('masterLockFee', e.target.value)}
                className="mt-1 bg-background text-foreground"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">TT Tax Distribution ({getCurrentNetworkSymbol()})</label>
              <Input
                type="number"
                step="0.001"
                value={localFees.networkFees[selectedNetwork]?.ttTaxDistribution || ''}
                onChange={(e) => handleFeeChange('ttTaxDistribution', e.target.value)}
                className="mt-1 bg-background text-foreground"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Logo Fee ({getCurrentNetworkSymbol()})</label>
              <Input
                type="number"
                step="0.001"
                value={localFees.networkFees[selectedNetwork]?.logoFee || ''}
                onChange={(e) => handleFeeChange('logoFee', e.target.value)}
                className="mt-1 bg-background text-foreground"
              />
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Liquidity Fees (Percentage of {getCurrentNetworkSymbol()})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground">Add Liquidity Fee (%)</label>
              <Input
                type="number"
                step="0.1"
                value={localFees.networkFees[selectedNetwork]?.addLiquidityFee || ''}
                onChange={(e) => handleFeeChange('addLiquidityFee', e.target.value)}
                className="mt-1 bg-background text-foreground"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Percentage fee charged on {getCurrentNetworkSymbol()} amount when adding liquidity
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Remove Liquidity Fee (%)</label>
              <Input
                type="number"
                step="0.1"
                value={localFees.networkFees[selectedNetwork]?.removeLiquidityFee || ''}
                onChange={(e) => handleFeeChange('removeLiquidityFee', e.target.value)}
                className="mt-1 bg-background text-foreground"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Percentage fee charged on {getCurrentNetworkSymbol()} amount when removing liquidity
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Fee Collectors</h3>
            <Button onClick={addCollector} size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Add Collector
            </Button>
          </div>

          <div className="space-y-4">
            {localFees.collectors.map((collector, index) => (
              <div key={index} className="flex gap-4 items-start">
                <div className="flex-grow">
                  <Input
                    value={collector.address}
                    onChange={(e) => handleCollectorChange(index, 'address', e.target.value)}
                    placeholder="Wallet address"
                    className="mb-2 bg-background text-foreground"
                  />
                  <Input
                    type="number"
                    value={collector.share}
                    onChange={(e) => handleCollectorChange(index, 'share', e.target.value)}
                    placeholder="Share percentage"
                    className="bg-background text-foreground"
                  />
                </div>
                {index > 0 && (
                  <Button
                    onClick={() => removeCollector(index)}
                    variant="destructive"
                    size="icon"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button
            onClick={saveFees}
            disabled={isLoading}
            className="w-full md:w-auto"
          >
            <Save className="h-4 w-4 mr-2" />
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
